import { useState } from 'react';
import { FaSlack } from 'react-icons/fa';

import { DirectoryChannelsEndpoint } from '@core/types/types.endpoint.directory';
import { SettingsAddToChannelsEndpoint } from '@core/types/types.endpoint.settings';
import { Button } from '@frontend/components/ui/button';
import { Checkbox } from '@frontend/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@frontend/components/ui/dialog';
import { Label } from '@frontend/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@frontend/components/ui/select';
import { useApi } from '@frontend/shared/hooks/shared.hook.useApi';
import { useWaitForChannels } from '@frontend/shared/hooks/shared.hook.useWaitForChannels';
import {
  useDirectoryChannels,
  useDirectoryTeam,
} from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';
import { getLoginUrl } from '@frontend/shared/util/util.getLoginUrl';
import { Loader2 } from 'lucide-react';

export function SettingsModalAddChannels({
  isReadOnly = false,
}: {
  isReadOnly?: boolean;
}) {
  const team = useDirectoryTeam();
  const [isOpen, setIsOpen] = useState(false);
  const [importDays, setImportDays] = useState('30');

  const waitForChannels = useWaitForChannels();
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const { data: channels, loading } = useApi<DirectoryChannelsEndpoint>(
    '/directory/channels',
    {},
    isOpen,
  );

  const existingChannels = useDirectoryChannels({
    excludeDeleted: true,
    requireQbInChannel: true,
  }).map((c) => c.id);

  const submitter = useSubmitter(async () => {
    const [, res] = await callApi<SettingsAddToChannelsEndpoint>(
      '/settings/addToChannels',
      {
        channelIds: selectedChannels,
        days: parseInt(importDays),
      },
    );

    if (res) {
      await waitForChannels(res.waitingForChannels);
      setIsOpen(false);
    }
  });

  const filteredChannels = channels?.filter(
    (c) => !existingChannels.includes(c.id),
  );

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={isReadOnly}
        onClick={() => {
          if (!isReadOnly) {
            if (
              !team.slack_scope ||
              !team.slack_scope.includes('channels:join')
            ) {
              window.location.href = getLoginUrl('bot', team);
            } else {
              setIsOpen(true);
            }
          }
        }}
      >
        <FaSlack className="mr-2 size-4" />
        Add to channels
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="max-h-[400px] overflow-y-auto pb-10">
            <h3 className="mt-8 text-sm font-semibold">Helpful link</h3>
            <Button variant="link" size="lg" className="mt-1" asChild>
              <a
                href="https://www.questionbase.com/docs/invite-to-channels"
                target="_blank"
                rel="noreferrer"
              >
                How to add the bot to private channels
              </a>
            </Button>

            <h3 className="mt-5 text-sm font-semibold">
              Add to public channels
            </h3>
            {!loading && filteredChannels?.length === 0 && (
              <p className="mt-1 italic">
                Already added to all public channels
              </p>
            )}
            {loading && <Loader2 className="mt-2 animate-spin" />}
            {!loading &&
              filteredChannels?.map((c) => (
                <div key={c.id} className="mt-1 flex items-center gap-2">
                  <Checkbox
                    id={`channel-${c.id}`}
                    checked={selectedChannels.includes(c.id)}
                    onCheckedChange={(checked) => {
                      setSelectedChannels(
                        checked
                          ? [...selectedChannels, c.id]
                          : selectedChannels.filter((id) => id !== c.id),
                      );
                    }}
                  />
                  <Label htmlFor={`channel-${c.id}`} className="cursor-pointer">
                    #{c.name}
                  </Label>
                </div>
              ))}
          </div>
          <DialogFooter className="border-t border-gray-200">
            {submitter.isSubmitting && (
              <div className="mr-auto">
                <p className="text-sm font-bold">
                  Waiting for bot to join channels...
                </p>
              </div>
            )}
            {!submitter.isSubmitting && (
              <div className="mr-auto">
                <p className="text-sm font-bold">
                  Import questions from the last
                </p>
                <Select
                  value={importDays}
                  onValueChange={setImportDays}
                  disabled={!selectedChannels.length || submitter.isSubmitting}
                >
                  <SelectTrigger size="sm" className="mt-1 max-w-[240px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">No, thanks</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={submitter.submit}
              disabled={!selectedChannels.length || submitter.isSubmitting}
            >
              {submitter.isSubmitting ? 'Adding...' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
